/**
 * Small display helpers shared by the Pro home panels.
 */
import { differenceInCalendarDays, format, isToday, isYesterday, parseISO } from 'date-fns';
import type { CalendarEvent } from '../../../types/calendar';

/** "10:30a" from "10:30 AM" / "10:30:00" / "10:30"; honors the no-time flags. */
export function formatEventTime(event: {
  reading_time?: string | null;
  no_time?: boolean;
  upon_adjournment?: boolean;
  upon_adjournment_of_committee_hearing?: boolean;
}): string {
  if (event.upon_adjournment || event.upon_adjournment_of_committee_hearing) return 'Adjourn';
  if (event.no_time || !event.reading_time) return '—';
  const raw = event.reading_time.trim();
  const ampm = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp])\.?[Mm]?\.?$/);
  if (ampm) {
    const hour = parseInt(ampm[1], 10);
    return `${hour}:${ampm[2]}${ampm[3].toLowerCase()}`;
  }
  const h24 = raw.match(/^(\d{1,2}):(\d{2})/);
  if (h24) {
    let hour = parseInt(h24[1], 10);
    const suffix = hour >= 12 ? 'p' : 'a';
    hour = hour % 12 || 12;
    return `${hour}:${h24[2]}${suffix}`;
  }
  return raw;
}

/** Strip any time component the API tacks onto a date string. */
export function toDateOnly(value: string): string {
  return value.slice(0, 10);
}

export function parseDate(value: string): Date {
  return parseISO(toDateOnly(value));
}

/** "Today · Saturday, August 1" / "Monday, August 3" for agenda group headers. */
export function formatAgendaDay(dateStr: string): string {
  const date = parseDate(dateStr);
  const label = format(date, 'EEEE, MMMM d');
  if (isToday(date)) return `Today · ${label}`;
  if (isYesterday(date)) return `Yesterday · ${label}`;
  return label;
}

/** "Today" / "Tomorrow" / "in 5 days" for the band's upcoming cards. */
export function formatRelativeDay(dateStr: string): string {
  const days = differenceInCalendarDays(parseDate(dateStr), new Date());
  if (days <= 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `in ${days} days`;
}

/** Short "8/10" style stamp for the change rail. */
export function formatChangeStamp(dateStr: string): string {
  const date = parseDate(dateStr);
  if (isToday(date)) return 'today';
  if (isYesterday(date)) return 'y’day';
  return format(date, 'M/d');
}

export type StatusTone = 'red' | 'green' | 'amber' | 'neutral';

/**
 * Classify a bill's latest action into a chip tone. The action strings come
 * from bill_history and are free text, so this is a keyword heuristic.
 */
export function classifyBillStatus(status: string | null | undefined): StatusTone {
  if (!status) return 'neutral';
  const s = status.toLowerCase();
  if (/postpone(d)? indefinitely|lost|defeated|killed|vetoed|failed/.test(s)) return 'red';
  if (/signed|became law|sent to the governor|passed|adopted|enacted/.test(s)) return 'green';
  return 'amber';
}

/**
 * Rank hot bills: hearing within 48h first, then a change in the last 24h,
 * then the rest; ties break on the soonest scheduled event.
 */
export function rankHotBills<
  T extends { lastUpdated?: string; nextCalendarEvent: { reading_date: string } | null },
>(bills: T[]): T[] {
  const now = Date.now();
  const rank = (bill: T): number => {
    if (bill.nextCalendarEvent) {
      const days = differenceInCalendarDays(parseDate(bill.nextCalendarEvent.reading_date), new Date());
      if (days >= 0 && days <= 2) return 0;
    }
    if (bill.lastUpdated && now - new Date(bill.lastUpdated).getTime() < 24 * 60 * 60 * 1000) {
      return 1;
    }
    return 2;
  };
  const nextEventTime = (bill: T): number =>
    bill.nextCalendarEvent ? parseDate(bill.nextCalendarEvent.reading_date).getTime() : Infinity;
  return [...bills].sort((a, b) => rank(a) - rank(b) || nextEventTime(a) - nextEventTime(b));
}

/** Events on a given yyyy-MM-dd day. */
export function eventsOnDay(events: CalendarEvent[], day: string): CalendarEvent[] {
  return events.filter((e) => toDateOnly(e.reading_date) === day);
}

/**
 * Workspace tag color. There is no stored workspace color, so assign one
 * deterministically from the workspace name — the same name hashes to the
 * same class everywhere. All classes are existing palette tokens.
 */
const WORKSPACE_DOT_CLASSES = [
  'bg-primary-400',
  'bg-party-democrat',
  'bg-success-600',
  'bg-warning-500',
  'bg-slate-500',
  'bg-party-republican',
  'bg-primary-700',
  'bg-slate-700',
];

export function workspaceDotClass(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return WORKSPACE_DOT_CLASSES[hash % WORKSPACE_DOT_CLASSES.length];
}
